import { useState, useMemo, useCallback } from 'react'
import { colors, btnPrimary } from '../../components/ui'
import { computeDiff, mergeSections, type DiffNode, type MergeSelection } from './config-diff'

interface Props {
  readonly centralConfig: Record<string, unknown>
  readonly reportedConfig: Record<string, unknown>
  readonly onSave: (merged: Record<string, unknown>) => void
  readonly onCancel: () => void
  readonly saving: boolean
}

type Source = 'left' | 'right'

const CONFIG_SECTIONS = [
  { key: 'deviceId', label: 'Identity' },
  { key: 'connection', label: 'Connection' },
  { key: 'protocolSettings', label: 'Protocol Settings' },
  { key: 'fieldMappings', label: 'Field Mappings' },
  { key: 'fieldDefaults', label: 'Field Defaults' },
  { key: 'directories', label: 'Directories' },
] as const

function isPlainObject(val: unknown): val is Record<string, unknown> {
  return val !== null && typeof val === 'object' && !Array.isArray(val)
}

function countDiffs(node: DiffNode): number {
  let count = 0
  for (const child of Object.values(node.children)) {
    if (child.status !== 'equal') count++
    if (child.children && Object.keys(child.children).length > 0) count += countDiffs(child)
  }
  return count
}

interface SelectionState {
  [key: string]: Source | SelectionState
}

function initSelections(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
  defaultSource: Source = 'right',
): SelectionState {
  const allKeys = new Set([...Object.keys(left), ...Object.keys(right)])
  const result: SelectionState = {}
  for (const key of allKeys) {
    const lVal = left[key]
    const rVal = right[key]
    if (isPlainObject(lVal) && isPlainObject(rVal)) {
      result[key] = initSelections(lVal, rVal, defaultSource)
    } else {
      result[key] = defaultSource
    }
  }
  return result
}

function setAllSource(sel: SelectionState, source: Source): SelectionState {
  const result: SelectionState = {}
  for (const [key, val] of Object.entries(sel)) {
    result[key] = typeof val === 'object' ? setAllSource(val as SelectionState, source) : source
  }
  return result
}

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 0.75rem',
  background: colors.bgMuted,
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600,
  marginBottom: '2px',
}

const fieldRowStyle = (isDiff: boolean): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: '180px 1fr 60px 1fr',
  gap: '0.5rem',
  padding: '0.35rem 0.75rem 0.35rem 1.5rem',
  fontSize: '12px',
  background: isDiff ? '#fffbeb' : 'transparent',
  borderBottom: `1px solid ${colors.border}`,
  alignItems: 'center',
})

const pickBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: '1px 6px',
  fontSize: '10px',
  border: `1px solid ${active ? colors.accent : colors.border}`,
  borderRadius: '3px',
  background: active ? '#eef4ff' : '#fff',
  color: active ? colors.accent : colors.textMuted,
  cursor: 'pointer',
  fontWeight: active ? 600 : 400,
})

function truncateValue(val: unknown): string {
  if (val === null || val === undefined) return 'null'
  if (typeof val === 'object') {
    const s = JSON.stringify(val)
    return s.length > 60 ? s.slice(0, 60) + '...' : s
  }
  return String(val)
}

export function ConfigTreeMerge({ centralConfig, reportedConfig, onSave, onCancel, saving }: Props) {
  const diff = useMemo(() => computeDiff(centralConfig, reportedConfig), [centralConfig, reportedConfig])
  const [selections, setSelections] = useState<SelectionState>(() =>
    initSelections(centralConfig, reportedConfig, 'right'),
  )
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const key of Object.keys(diff.children)) {
      if (diff.children[key].hasDifferences) initial.add(key)
    }
    return initial
  })

  const toggleExpand = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const setSectionSource = useCallback((sectionKey: string, source: Source) => {
    setSelections((prev) => {
      const sectionSel = prev[sectionKey]
      if (typeof sectionSel === 'object') {
        return { ...prev, [sectionKey]: setAllSource(sectionSel as SelectionState, source) }
      }
      return { ...prev, [sectionKey]: source }
    })
  }, [])

  const setFieldSource = useCallback((sectionKey: string, fieldKey: string, source: Source) => {
    setSelections((prev) => {
      const sectionSel = prev[sectionKey]
      if (typeof sectionSel === 'object') {
        return {
          ...prev,
          [sectionKey]: { ...(sectionSel as SelectionState), [fieldKey]: source },
        }
      }
      return prev
    })
  }, [])

  const mergedResult = useMemo(
    () => mergeSections(centralConfig, reportedConfig, selections as Record<string, MergeSelection>),
    [centralConfig, reportedConfig, selections],
  )

  const renderSection = (key: string, label: string) => {
    const node = diff.children[key]
    if (!node) return null
    const isExpanded = expanded.has(key)
    const diffCount = countDiffs(node)
    const lObj = isPlainObject(centralConfig[key]) ? (centralConfig[key] as Record<string, unknown>) : {}
    const rObj = isPlainObject(reportedConfig[key]) ? (reportedConfig[key] as Record<string, unknown>) : {}

    return (
      <div key={key} style={{ marginBottom: '0.25rem' }}>
        <div style={sectionHeaderStyle} onClick={() => toggleExpand(key)}>
          <span>
            {isExpanded ? '▾' : '▸'} {label}
            {diffCount > 0 && (
              <span style={{ marginLeft: '8px', fontSize: '11px', color: '#92400e', fontWeight: 400 }}>
                {diffCount} difference{diffCount !== 1 ? 's' : ''}
              </span>
            )}
            {diffCount === 0 && (
              <span style={{ marginLeft: '8px', fontSize: '11px', color: '#065f46', fontWeight: 400 }}>identical</span>
            )}
          </span>
          <span style={{ display: 'flex', gap: '4px' }}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setSectionSource(key, 'left')
              }}
              style={pickBtnStyle(false)}
            >
              Central
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setSectionSource(key, 'right')
              }}
              style={pickBtnStyle(false)}
            >
              Reported
            </button>
          </span>
        </div>
        {isExpanded && node.children && (
          <div>
            {Object.entries(node.children).map(([fieldKey, fieldNode]) => {
              const isDiff = fieldNode.status !== 'equal'
              const sectionSel = selections[key]
              const currentSource =
                typeof sectionSel === 'object' ? (sectionSel as SelectionState)[fieldKey] : sectionSel
              return (
                <div key={fieldKey} style={fieldRowStyle(isDiff)}>
                  <span style={{ fontWeight: 500, color: colors.textMuted }}>{fieldKey}</span>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncateValue(lObj[fieldKey])}
                  </span>
                  {isDiff ? (
                    <span style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={() => setFieldSource(key, fieldKey, 'left')}
                        style={pickBtnStyle(currentSource === 'left')}
                        title="Use Central"
                      >
                        L
                      </button>
                      <button
                        type="button"
                        onClick={() => setFieldSource(key, fieldKey, 'right')}
                        style={pickBtnStyle(currentSource === 'right')}
                        title="Use Reported"
                      >
                        R
                      </button>
                    </span>
                  ) : (
                    <span style={{ textAlign: 'center', color: colors.textMuted, fontSize: '10px' }}>=</span>
                  )}
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncateValue(rObj[fieldKey])}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const topLevelKeys = Object.keys(diff.children)

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        {topLevelKeys.map((key) => {
          const section = CONFIG_SECTIONS.find((s) => s.key === key)
          return renderSection(key, section?.label ?? key)
        })}
      </div>
      <details style={{ marginBottom: '1rem' }}>
        <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: colors.textMuted }}>
          Merged Result Preview
        </summary>
        <pre
          style={{
            background: colors.bgMuted,
            padding: '0.75rem',
            borderRadius: '4px',
            fontSize: '11px',
            overflow: 'auto',
            maxHeight: '300px',
            border: `1px solid ${colors.border}`,
            marginTop: '0.5rem',
          }}
        >
          {JSON.stringify(mergedResult, null, 2)}
        </pre>
      </details>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{ ...btnPrimary, background: '#fff', color: colors.text, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(mergedResult)}
          disabled={saving}
          style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
        >
          {saving ? 'Saving...' : 'Save Merged Config'}
        </button>
      </div>
    </div>
  )
}
